import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, DataTable, Pagination, useAuth } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import type { DataTableColumn } from '@aaas/shared';
import { useCVListQuery } from '../../hooks/useCVQueries';
import { useATSRole } from '../../hooks/useATSRole';
import { CVFilterBar, type CVFilters } from '../../components/CVFilterBar';
import type { CV, CVProcessingStatus } from '../../types';
import { PROCESSING_STATUS_CONFIG, ATS_ROLE_LABELS, ATS_ROLE_COLORS, ATSRole } from '../../types';

// Filter keys for URL params
const FILTER_KEYS = ['search', 'processingStatus'];
// Array filter keys for multi-select
const ARRAY_FILTER_KEYS = ['poolIds', 'positionIds', 'jobIds', 'cvTagSlugs'];

// Status badge component
function StatusBadge({ status }: { status: CVProcessingStatus }) {
  const config = PROCESSING_STATUS_CONFIG[status];
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[config.color]}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          config.color === 'gray'
            ? 'bg-gray-500'
            : config.color === 'blue'
              ? 'bg-blue-500'
              : config.color === 'green'
                ? 'bg-green-500'
                : 'bg-red-500'
        }`}
      />
      {config.label}
    </span>
  );
}

// Clickable chip component
function Chip({ label, onClick }: { label: string; onClick?: () => void }) {
  const baseClasses =
    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700';
  const clickableClasses = onClick ? 'cursor-pointer hover:bg-gray-200 transition-colors' : '';

  return (
    <span className={`${baseClasses} ${clickableClasses}`} onClick={onClick}>
      {label}
    </span>
  );
}

export function CVListPage() {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { t } = useTranslation('ats');
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({
      filterKeys: FILTER_KEYS,
      arrayFilterKeys: ARRAY_FILTER_KEYS,
    });

  // Get current user's role for role indicator
  const { role: currentUserRole } = useATSRole();

  // Map URL filters to CVFilters type
  const cvFilters: CVFilters = useMemo(
    () => ({
      search: filters.search as string | undefined,
      processingStatus: filters.processingStatus as string | undefined,
      poolIds: filters.poolIds as string[] | undefined,
      positionIds: filters.positionIds as string[] | undefined,
      jobIds: filters.jobIds as string[] | undefined,
      cvTagSlugs: filters.cvTagSlugs as string[] | undefined,
    }),
    [filters]
  );

  // Wrapper to convert CVFilters to Record for URL state
  const handleFiltersChange = useCallback(
    (newFilters: CVFilters) => {
      setFilters(newFilters as Record<string, string | string[] | undefined>);
    },
    [setFilters]
  );

  // React Query for CV list
  const {
    data: cvResponse,
    isLoading,
    isFetching,
    error,
  } = useCVListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    poolIds: cvFilters.poolIds,
    positionIds: cvFilters.positionIds,
    jobIds: cvFilters.jobIds,
    processingStatus: cvFilters.processingStatus,
    search: cvFilters.search,
    cvTagSlugs: cvFilters.cvTagSlugs,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  // Extract data from response
  const cvs = cvResponse?.data?.items || [];
  const totalItems = cvResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCVClick = useCallback(
    (record: CV) => {
      navigate(`/ats/cvs/${record.id}`);
    },
    [navigate]
  );

  // Define table columns
  const columns: DataTableColumn<CV>[] = [
    {
      key: 'name',
      title: t('cv.columns.name'),
      sortable: true,
      render: (value: string, record: CV) => (
        <button
          onClick={() => handleCVClick(record)}
          className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-left"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'phone',
      title: t('cv.columns.phone'),
      width: 140,
      render: (value: string | null | undefined) => (
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || '-'}</span>
      ),
    },
    {
      key: 'processing_status',
      title: t('cv.columns.reviewStatus'),
      width: 120,
      render: (value: CVProcessingStatus) => <StatusBadge status={value} />,
    },
    {
      key: 'pools',
      title: t('nav.pools'),
      render: (_value, record: CV) => {
        if (!record.pools || record.pools.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.pools.map((pool) => (
              <Chip key={pool.id} label={pool.name} />
            ))}
          </div>
        );
      },
    },
    {
      key: 'positions',
      title: t('nav.positions'),
      render: (_value, record: CV) => {
        // Derive positions from jobs if M2M positions is empty
        const positions =
          record.positions && record.positions.length > 0
            ? record.positions
            : (record.jobs || [])
                .map((j) => ({ id: j.id, name: j.position_name }))
                .filter((p, i, arr) => arr.findIndex((x) => x.name === p.name) === i);

        if (positions.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {positions.map((position) => (
              <Chip key={position.id} label={position.name} />
            ))}
          </div>
        );
      },
    },
    {
      key: 'jobs',
      title: t('nav.jobs'),
      render: (_value, record: CV) => {
        if (!record.jobs || record.jobs.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.jobs.map((job) => (
              <Chip
                key={job.id}
                label={`${job.code}${job.campaign_name ? ` (${job.campaign_name})` : ''}`}
              />
            ))}
          </div>
        );
      },
    },
    {
      key: 'created_at',
      title: t('cv.columns.uploadedAt'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  if (isLoading && !cvResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-teal-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('cv.pageTitle')}</h1>
              {currentUserRole && (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ATS_ROLE_COLORS[currentUserRole as ATSRole].bg} ${ATS_ROLE_COLORS[currentUserRole as ATSRole].text}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full mr-1.5 ${ATS_ROLE_COLORS[currentUserRole as ATSRole].dot}`}
                  />
                  {ATS_ROLE_LABELS[currentUserRole as ATSRole]}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{t('cv.pageDescription')}</p>
          </div>
          {isFetching && !isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <CVFilterBar filters={cvFilters} onFiltersChange={handleFiltersChange} />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('cv.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={cvs}
          loading={isLoading && !cvResponse}
          rowKey={(record) => record.id}
          emptyMessage={t('cv.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>
    </div>
  );
}
